import React, { useState, useEffect } from 'react';
import { FileText, Check, Clock, Filter, Search, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { getBackendUrl } from '../utils/config';

// Card de documento (componente separado)
const DocumentoCard = ({ doc, isPendente, onAbrir, formatarData }) => (
  <Card className={`hover:shadow-lg transition-shadow ${isPendente ? 'border-orange-300' : ''}`}>
    <CardHeader className={isPendente ? 'bg-orange-50' : ''}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {doc.titulo}
            {isPendente && (
              <Badge variant="destructive" className="ml-2">
                Pendente
              </Badge>
            )}
          </CardTitle>
          <div className="flex gap-2 mt-2">
            <Badge variant="outline">{doc.categoria}</Badge>
            <span className="text-sm text-gray-500">{formatarData(doc.data_publicacao)}</span>
          </div>
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-4">
      <p className="text-gray-600 text-sm mb-4">{doc.descricao}</p>
      
      <div className="flex gap-2">
        <Button
          onClick={() => onAbrir(doc)}
          className="flex-1"
          variant={isPendente ? 'default' : 'outline'}
        >
          <Eye className="w-4 h-4 mr-2" />
          {isPendente ? 'Ler e Confirmar' : 'Ver Documento'}
        </Button>
      </div>
      
      <div className="text-xs text-gray-500 mt-2">
        Autor: {doc.autor}
      </div>
    </CardContent>
  </Card>
);

const DocumentosTab = ({ user }) => {
  const [documentosPendentes, setDocumentosPendentes] = useState([]);
  const [documentosConsulta, setDocumentosConsulta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pendentes'); // pendentes ou consulta
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [documentoAberto, setDocumentoAberto] = useState(null);
  const [tempoVisualizacao, setTempoVisualizacao] = useState(0);
  const [inicioVisualizacao, setInicioVisualizacao] = useState(null);

  // URL centralizado
  const API_URL = getBackendUrl();
  
  const CATEGORIAS = [
    'Comunicados Internos',
    'Procedimentos',
    'Normas',
    'Atas',
    'Outros'
  ];

  // Carregar documentos
  const carregarDocumentos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const params = filtroCategoria ? `?categoria=${encodeURIComponent(filtroCategoria)}` : '';
      
      const [resPendentes, resConsulta] = await Promise.all([
        fetch(`${API_URL}/api/documentos/pendentes${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/documentos/consulta${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (resPendentes.ok && resConsulta.ok) {
        const pendentes = await resPendentes.json();
        const consulta = await resConsulta.json();
        setDocumentosPendentes(pendentes);
        setDocumentosConsulta(consulta);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const loadDocs = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        
        const params = filtroCategoria ? `?categoria=${encodeURIComponent(filtroCategoria)}` : '';
        
        const [resPendentes, resConsulta] = await Promise.all([
          fetch(`${API_URL}/api/documentos/pendentes${params}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/documentos/consulta${params}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (resPendentes.ok && resConsulta.ok) {
          const pendentes = await resPendentes.json();
          const consulta = await resConsulta.json();
          setDocumentosPendentes(pendentes);
          setDocumentosConsulta(consulta);
        }
      } catch (error) {
        console.error('Erro ao carregar documentos:', error);
      }
      setLoading(false);
    };
    
    loadDocs();
  }, [filtroCategoria, API_URL]);

  // Timer de visualização
  useEffect(() => {
    let interval;
    if (documentoAberto && inicioVisualizacao) {
      interval = setInterval(() => {
        const agora = Date.now();
        const segundos = Math.floor((agora - inicioVisualizacao) / 1000);
        setTempoVisualizacao(segundos);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [documentoAberto, inicioVisualizacao]);

  // Abrir documento
  const abrirDocumento = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/documentos/${doc.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const docCompleto = await res.json();
        setDocumentoAberto(docCompleto);
        setInicioVisualizacao(Date.now());
        setTempoVisualizacao(0);
      }
    } catch (error) {
      console.error('Erro ao abrir documento:', error);
    }
  };

  // Confirmar leitura
  const confirmarLeitura = async () => {
    if (tempoVisualizacao < 1) {
      alert('⚠️ Aguarde pelo menos 1 segundo antes de confirmar a leitura');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/documentos/${documentoAberto.id}/confirmar?tempo_visualizacao=${tempoVisualizacao}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (res.ok) {
        alert('✅ Leitura confirmada com sucesso!');
        setDocumentoAberto(null);
        setInicioVisualizacao(null);
        setTempoVisualizacao(0);
        carregarDocumentos();
      } else {
        const error = await res.json();
        alert(`❌ ${error.detail}`);
      }
    } catch (error) {
      console.error('Erro ao confirmar leitura:', error);
      alert('❌ Erro ao confirmar leitura');
    }
  };

  // Registar fim de visualização
  const registarFimVisualizacao = async () => {
    if (!documentoAberto || tempoVisualizacao < 1) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${API_URL}/api/documentos/${documentoAberto.id}/fim-visualizacao?tempo_segundos=${tempoVisualizacao}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Erro ao registar fim de visualização:', error);
    }
  };

  // Fechar documento
  const fecharDocumento = async () => {
    await registarFimVisualizacao();
    setDocumentoAberto(null);
    setInicioVisualizacao(null);
    setTempoVisualizacao(0);
  };

  // Formatar data
  const formatarData = (isoString) => {
    if (!isoString) return 'N/A';
    const data = new Date(isoString);
    return data.toLocaleDateString('pt-PT');
  };

  // Modal de visualização
  if (documentoAberto) {
    const isPendente = documentosPendentes.some(d => d.id === documentoAberto.id);
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl max-h-[90vh] overflow-auto">
          <CardHeader className="bg-blue-50">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{documentoAberto.titulo}</CardTitle>
                <div className="flex gap-2 mt-2">
                  <Badge>{documentoAberto.categoria}</Badge>
                  <span className="text-sm text-gray-600">
                    {formatarData(documentoAberto.data_publicacao)}
                  </span>
                </div>
              </div>
              <Button
                onClick={fecharDocumento}
                variant="ghost"
              >
                ✕
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Timer de visualização */}
            {isPendente && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <div>
                    <div className="font-semibold text-yellow-800">
                      Tempo de visualização: {tempoVisualizacao} segundos
                    </div>
                    <div className="text-sm text-yellow-700">
                      Leia o documento antes de confirmar (mínimo 1 segundo)
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Conteúdo */}
            <div className="prose max-w-none mb-6">
              <p className="text-gray-700 whitespace-pre-wrap">
                {documentoAberto.conteudo_texto || documentoAberto.descricao}
              </p>
            </div>

            {/* Ficheiro anexo - Visualização inline (SEM download para colaboradores) */}
            {documentoAberto.ficheiro_nome && (
              <div className="mb-6">
                {/* Cabeçalho do ficheiro */}
                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">{documentoAberto.ficheiro_nome}</span>
                  {documentoAberto.ficheiro_existe === false && (
                    <span className="text-red-600 text-sm">⚠️ Em falta</span>
                  )}
                </div>
                
                {/* Visualizador PDF inline ou mensagem de erro */}
                {documentoAberto.ficheiro_existe === false ? (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
                    <FileText className="w-12 h-12 text-orange-300 mx-auto mb-3" />
                    <h3 className="text-orange-800 font-semibold mb-2">
                      Ficheiro temporariamente indisponível
                    </h3>
                    <p className="text-orange-600 text-sm">
                      O administrador foi notificado e irá disponibilizar o ficheiro brevemente.
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '600px' }}>
                    <iframe
                      src={`${API_URL}/api/documentos/${documentoAberto.id}/download?token=${localStorage.getItem('token')}`}
                      className="w-full h-full border-0"
                      title={documentoAberto.ficheiro_nome}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Botão confirmar */}
            {isPendente && (
              <div className="flex gap-3">
                <Button
                  onClick={confirmarLeitura}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  size="lg"
                  disabled={tempoVisualizacao < 1}
                >
                  <Check className="w-5 h-5 mr-2" />
                  Confirmar Leitura
                </Button>
                <Button
                  onClick={fecharDocumento}
                  variant="outline"
                  size="lg"
                >
                  Fechar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Cabeçalho */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Documentos</h2>
        <p className="text-gray-600">Consulte e confirme a leitura de documentos</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          onClick={() => setView('pendentes')}
          variant={view === 'pendentes' ? 'default' : 'outline'}
          className="relative"
        >
          Pendentes de Leitura
          {documentosPendentes.length > 0 && (
            <Badge className="ml-2 bg-red-500">{documentosPendentes.length}</Badge>
          )}
        </Button>
        <Button
          onClick={() => setView('consulta')}
          variant={view === 'consulta' ? 'default' : 'outline'}
        >
          Para Consulta
        </Button>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas as Categorias</option>
              {CATEGORIAS.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">A carregar documentos...</div>
        </div>
      )}

      {/* Lista de documentos */}
      {!loading && (
        <div className="space-y-4">
          {view === 'pendentes' ? (
            documentosPendentes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Check className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-semibold">Nenhum documento pendente!</p>
                  <p className="text-sm mt-2">Todos os documentos foram confirmados</p>
                </CardContent>
              </Card>
            ) : (
              documentosPendentes.map(doc => (
                <DocumentoCard 
                  key={doc.id} 
                  doc={doc} 
                  isPendente={true}
                  onAbrir={abrirDocumento}
                  formatarData={formatarData}
                />
              ))
            )
          ) : (
            documentosConsulta.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Nenhum documento para consulta</p>
                </CardContent>
              </Card>
            ) : (
              documentosConsulta.map(doc => (
                <DocumentoCard 
                  key={doc.id} 
                  doc={doc} 
                  isPendente={false}
                  onAbrir={abrirDocumento}
                  formatarData={formatarData}
                />
              ))
            )
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentosTab;
